import { CalendarOutlined } from '@ant-design/icons';
import { DatePicker, Popover } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';

interface ActionPlanDateTimePickerProps {
  value?: [Dayjs | null, Dayjs | null];
  onChange?: (dates: [Dayjs | null, Dayjs | null]) => void;
  initialRange?: [Dayjs, Dayjs] | null;
  // Giới hạn cho parent khi đã có childs:
  // - startDate không được lớn hơn minStartAllowed (min ngày bắt đầu của các con)
  // - endDate không được nhỏ hơn maxEndAllowed (max ngày kết thúc của các con)
  minStartAllowed?: Dayjs | null;
  maxEndAllowed?: Dayjs | null;
  // Disable các ngày trong range của các childs (để parent không thể chọn các ngày này)
  disabledChildRanges?: Array<{ start: Dayjs; end: Dayjs }> | null;
  /** Hover ngày bị khóa vì cha/con — hiện warning (cây thời gian). */
  disabledDateHint?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** mode = 'range' (mặc định) hoặc 'single' để hiển thị 1 picker */
  mode?: 'range' | 'single';
}

const ActionPlanDateTimePicker: React.FC<ActionPlanDateTimePickerProps> = ({
  value,
  onChange,
  initialRange,
  minStartAllowed = null,
  maxEndAllowed = null,
  disabledChildRanges = null,
  disabledDateHint,
  disabled = false,
  className = '',
  style = {},
  mode = 'range',
}) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(value?.[0] || null);
  const [endDate, setEndDate] = useState<Dayjs | null>(value?.[1] || null);
  const isSingle = mode === 'single';

  useEffect(() => {
    if (value) {
      setStartDate(value[0]);
      setEndDate(value[1]);
    }
  }, [value]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-date-picker-panel-only .ant-picker-input {
        display: none !important;
      }
      .custom-date-picker-panel-only .ant-picker-panel {
        margin-top: 0 !important;
        position: static !important;
        box-shadow: none !important;
        background: #fff !important;
      }
      .custom-date-picker-panel-only .ant-picker {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
      }
      .custom-date-picker-panel-only .ant-picker-dropdown {
        box-shadow: none !important;
      }
      .custom-date-picker-panel-only .ant-picker-panel-container {
        box-shadow: none !important;
      }
      .custom-date-picker-wrapper {
        position: relative;
      }
      .custom-date-picker-wrapper .ant-picker-dropdown {
        position: static !important;
        inset: auto !important;
        transform: none !important;
        box-shadow: none !important;
      }
      .custom-date-picker-wrapper .ant-picker-panel-container {
        box-shadow: none !important;
      }
      .custom-date-picker-panel-only * {
        box-shadow: none !important;
      }
      .custom-date-picker-wrapper * {
        box-shadow: none !important;
      }

      /* Make prev/next month buttons bolder */
      .custom-date-picker-panel-only .ant-picker-header button {
        opacity: 0.95;
      }
      .custom-date-picker-panel-only .ant-picker-header button:hover {
        opacity: 1;
      }
      .custom-date-picker-panel-only .ant-picker-header button .ant-picker-next-icon,
      .custom-date-picker-panel-only .ant-picker-header button .ant-picker-prev-icon,
      .custom-date-picker-panel-only .ant-picker-header button .ant-picker-super-next-icon,
      .custom-date-picker-panel-only .ant-picker-header button .ant-picker-super-prev-icon {
          color: rgba(113, 108, 108, 0.9) !important;
        font-weight: 400 !important;
      }

      /* Make "other month" dates red + bold.
         Popup calendar is rendered inside .custom-date-picker-wrapper .ant-picker-dropdown (sibling of panel-only). */
      .custom-date-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell:not(.ant-picker-cell-in-view),
      .custom-date-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell:not(.ant-picker-cell-in-view) .ant-picker-cell-inner {
        opacity: 1 !important;
      }
      .custom-date-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell:not(.ant-picker-cell-in-view) .ant-picker-cell-inner,
      .custom-date-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell-disabled:not(.ant-picker-cell-in-view) .ant-picker-cell-inner {
          color: rgba(113, 108, 108, 0.9) !important;
        font-weight: 400 !important;
      }

      .ap-dtp-has-disabled-hint .ant-picker-cell-disabled {
        pointer-events: auto !important;
        cursor: not-allowed;
      }
      .ap-dtp-disabled-hint-cell {
        display: block;
        width: 100%;
        height: 100%;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Format display text based on rules
  const formatDisplayText = (start: Dayjs | null, end: Dayjs | null): string => {
    if (isSingle) {
      const base = start || dayjs();
      const day = base.date();
      const month = base.month() + 1;
      const year = base.year();
      const currentYear = dayjs().year();

      // Nếu cùng năm hiện tại thì ẩn năm, chỉ hiển thị DD/MM
      if (year === currentYear) {
        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
      }

      // Khác năm thì hiển thị cả năm
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }

    if (!start || !end) return '';

    const startDay = start.date();
    const startMonth = start.month() + 1; // dayjs month is 0-indexed
    const startYear = start.year();

    const endDay = end.date();
    const endMonth = end.month() + 1;
    const endYear = end.year();

    // Same year, same month, same day
    if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
      return `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')}`;
    }

    // Same year, same month
    if (startYear === endYear && startMonth === endMonth) {
      return `${String(startDay).padStart(2, '0')} - ${String(endDay).padStart(2, '0')}/${String(startMonth).padStart(
        2,
        '0'
      )}`;
    }

    // Same year, different months
    if (startYear === endYear) {
      return `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')} - ${String(endDay).padStart(
        2,
        '0'
      )}/${String(endMonth).padStart(2, '0')}`;
    }

    // Different years
    return `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')}/${startYear} - ${String(
      endDay
    ).padStart(2, '0')}/${String(endMonth).padStart(2, '0')}/${endYear}`;
  };

  const displayText = formatDisplayText(startDate, endDate);

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    if (isSingle) {
      if (onChange) {
        onChange([date, date]);
      }
    } else if (onChange) {
      onChange([date, endDate]);
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    if (onChange) {
      onChange([startDate, date]);
    }
  };

  // Disable cho ô "Bắt đầu"
  const disabledStartDate = (current: Dayjs): boolean => {
    let disabledFlag = false;

    // Không được chọn ngày bắt đầu sau ngày kết thúc (nếu đã có endDate)
    if (endDate) {
      disabledFlag = disabledFlag || current.isAfter(endDate, 'day');
    }

    // Rule cho child: chỉ được chọn trong khoảng initialRange (range của parent)
    if (initialRange && initialRange.length === 2) {
      const [initStart, initEnd] = initialRange;
      const s = dayjs(initStart);
      const e = dayjs(initEnd);
      disabledFlag = disabledFlag || current.isBefore(s, 'day') || current.isAfter(e, 'day');
    }

    // Rule cho parent khi có childs: start không được sau ngày bắt đầu nhỏ nhất của con
    if (minStartAllowed) {
      const minStart = dayjs(minStartAllowed);
      disabledFlag = disabledFlag || current.isAfter(minStart, 'day');
    }

    // Disable các ngày nằm giữa range con (không khóa 2 đầu mút): cha phải chọn được ngày = bắt đầu/kết thúc của con
    if (disabledChildRanges && disabledChildRanges.length > 0) {
      const isStrictlyInsideChildRange = disabledChildRanges.some((range) => {
        const rangeStart = dayjs(range.start).startOf('day');
        const rangeEnd = dayjs(range.end).startOf('day');
        return current.isAfter(rangeStart, 'day') && current.isBefore(rangeEnd, 'day');
      });
      disabledFlag = disabledFlag || isStrictlyInsideChildRange;
    }

    return disabledFlag;
  };

  // Disable cho ô "Kết thúc"
  const disabledEndDate = (current: Dayjs): boolean => {
    let disabledFlag = false;

    // Không được chọn ngày kết thúc trước ngày bắt đầu (nếu đã có startDate)
    if (startDate) {
      disabledFlag = disabledFlag || current.isBefore(startDate, 'day');
    }

    // Rule cho child: chỉ được chọn trong khoảng initialRange (range của parent)
    if (initialRange && initialRange.length === 2) {
      const [initStart, initEnd] = initialRange;
      const s = dayjs(initStart);
      const e = dayjs(initEnd);
      disabledFlag = disabledFlag || current.isBefore(s, 'day') || current.isAfter(e, 'day');
    }

    // Rule cho parent khi có childs: end không được trước ngày kết thúc lớn nhất của con
    if (maxEndAllowed) {
      const maxEnd = dayjs(maxEndAllowed);
      disabledFlag = disabledFlag || current.isBefore(maxEnd, 'day');
    }

    // Disable các ngày nằm giữa range con (không khóa 2 đầu mút): cha phải chọn được ngày = bắt đầu/kết thúc của con
    if (disabledChildRanges && disabledChildRanges.length > 0) {
      const isStrictlyInsideChildRange = disabledChildRanges.some((range) => {
        const rangeStart = dayjs(range.start).startOf('day');
        const rangeEnd = dayjs(range.end).startOf('day');
        return current.isAfter(rangeStart, 'day') && current.isBefore(rangeEnd, 'day');
      });
      disabledFlag = disabledFlag || isStrictlyInsideChildRange;
    }

    return disabledFlag;
  };

  const isInsideDisabledChildRange = (current: Dayjs): boolean => {
    if (!disabledChildRanges?.length) return false;
    return disabledChildRanges.some((range) => {
      const rangeStart = dayjs(range.start).startOf('day');
      const rangeEnd = dayjs(range.end).startOf('day');
      return current.isAfter(rangeStart, 'day') && current.isBefore(rangeEnd, 'day');
    });
  };

  const isBoundConstraintDisabled = (current: Dayjs, kind: 'start' | 'end'): boolean => {
    if (initialRange && initialRange.length === 2) {
      const s = dayjs(initialRange[0]);
      const e = dayjs(initialRange[1]);
      if (current.isBefore(s, 'day') || current.isAfter(e, 'day')) return true;
    }
    if (kind === 'start' && minStartAllowed && current.isAfter(dayjs(minStartAllowed), 'day')) {
      return true;
    }
    if (kind === 'end' && maxEndAllowed && current.isBefore(dayjs(maxEndAllowed), 'day')) {
      return true;
    }
    return isInsideDisabledChildRange(current);
  };

  const renderConstraintHintCell = (kind: 'start' | 'end') => {
    const renderCell = (
      current: Dayjs | string | number,
      info: { originNode: React.ReactElement }
    ) => {
      const date = dayjs(current);
      if (!disabledDateHint || !date.isValid() || !isBoundConstraintDisabled(date, kind)) {
        return info.originNode;
      }
      return (
        <Popover
          trigger="hover"
          placement="top"
          mouseEnterDelay={0.12}
          mouseLeaveDelay={0.08}
          overlayClassName="qc-date-range-hint-popover"
          getPopupContainer={() => document.body}
          zIndex={4000}
          content={disabledDateHint}
        >
          <div className="ap-dtp-disabled-hint-cell">{info.originNode}</div>
        </Popover>
      );
    };
    return renderCell;
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const singleContent = (
    <div style={{ padding: '12px 16px', minWidth: '260px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '-8px', gap: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)', minWidth: '70px' }}>
          Ngày
        </div>
        <div style={{ flex: 1, pointerEvents: 'none' }}>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            // variant="borderless"
            size="small"
          />
        </div>
      </div>
      <div className={`custom-date-picker-wrapper${disabledDateHint ? ' ap-dtp-has-disabled-hint' : ''}`}>
        <DatePicker
          value={startDate}
          onChange={handleStartDateChange}
          format="DD/MM/YYYY"
          open
          variant="borderless"
          getPopupContainer={(trigger) => trigger.parentElement || document.body}
          style={{ width: '100%' }}
          className="custom-date-picker-panel-only"
          cellRender={disabledDateHint ? renderConstraintHintCell('start') : undefined}
        />
      </div>
    </div>
  );

  const rangeContent = (
    <div style={{ display: 'flex', gap: '20px', padding: '16px', minWidth: '636px' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '-8px', gap: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)', minWidth: '70px' }}>
            Bắt đầu
          </div>
          <div style={{ flex: 1, pointerEvents: 'none' }}>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              format="DD/MM/YYYY"
              disabledDate={(current: Dayjs) => disabledStartDate(current)}
              style={{ width: '100%' }}
              bordered
              size="small"
            />
          </div>
        </div>
        <div className={`custom-date-picker-wrapper${disabledDateHint ? ' ap-dtp-has-disabled-hint' : ''}`}>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY"
            disabledDate={(current: Dayjs) => disabledStartDate(current)}
            open
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            style={{ width: '100%' }}
            bordered={false}
            className="custom-date-picker-panel-only"
            cellRender={disabledDateHint ? renderConstraintHintCell('start') : undefined}
          />
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '-8px', gap: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)', minWidth: '70px' }}>
            Kết thúc
          </div>
          <div style={{ flex: 1, pointerEvents: 'none' }}>
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              format="DD/MM/YYYY"
              disabledDate={(current: Dayjs) => disabledEndDate(current)}
              style={{ width: '100%' }}
              bordered
              size="small"
            />
          </div>
        </div>
        <div className={`custom-date-picker-wrapper${disabledDateHint ? ' ap-dtp-has-disabled-hint' : ''}`}>
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            format="DD/MM/YYYY"
            disabledDate={(current: Dayjs) => disabledEndDate(current)}
            open
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            style={{ width: '100%' }}
            bordered={false}
            className="custom-date-picker-panel-only"
            cellRender={disabledDateHint ? renderConstraintHintCell('end') : undefined}
          />
        </div>
      </div>
    </div>
  );

  const content = isSingle ? singleContent : rangeContent;
  return (
    <Popover
      content={content}
      trigger="click"
      open={open && !disabled}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
      // overlayStyle={{ padding: 0, maxWidth: 'calc(100vw - 32px)' }}
      // overlayInnerStyle={{
      //   padding: 0,
      //   height: 'auto',
      //   overflow: 'visible',
      //   borderRadius: '8px',
      //   boxShadow:
      //     '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
      // }}
    >
      <div
        className={`w-full cursor-pointer ${className}`}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          minHeight: '32px',
          border: 'none',
          boxShadow: 'none',
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <CalendarOutlined style={{ color: '#42b814' }} />

        <span style={{ marginLeft: '4px', color: displayText ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.25)' }}>
          {displayText || 'Chọn ngày'}
        </span>
      </div>
    </Popover>
  );
};

export default ActionPlanDateTimePicker;
